import React, { useContext } from "react";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AlignVerticalBottomIcon from "@mui/icons-material/AlignVerticalBottom";
import {
  Box,
  Typography,
  Stack,
  Card,
  CardActionArea,
  AvatarGroup,
  Avatar,
  Chip,
} from "@mui/material";
import { Link } from "react-router-dom";
import { getUser } from "../utility/utility";
import { UserContext } from "../components/context/UserContext";

const MeetingCard = ({ meetingItem }) => {
  const { user } = useContext(UserContext);
  const members = [
    ...(meetingItem.selectedMembers || []),
    ...(meetingItem.selectedOtherMembers || []),
  ];

  const status = meetingItem.status;

  // Custom soft palettes for state tracking chips
  const statusConfig = {
    requested: { bg: "#e0f2fe", text: "#0369a1" },
    accepted: { bg: "#dcfce7", text: "#15803d" },
    cancelled: { bg: "#fee2e2", text: "#b91c1c" },
  }[status] || { bg: "#f1f5f9", text: "#475569" };

  return (
    <Card
      component={Link}
      to={`/${user?.role || "exhibitor"}/meeting/${meetingItem._id}`}
      elevation={0}
      sx={{
        display: "block",
        textDecoration: "none",
        position: "relative",
        borderRadius: 3,
        backgroundColor: "#ffffff",
        // The Modern Border Base State
        border: "1px solid #f9e6d3",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-2px)",
          // Smooth color-shifting active border frame
          borderColor: "#0288d1",
          boxShadow: "0 10px 25px rgba(2, 136, 209, 0.08)",
        },
      }}
    >
      <CardActionArea sx={{ p: 2.5 }}>
        <Stack spacing={2}>
          {/* Header Action Strip */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                lineHeight: 1.3,
                maxHeight: "2.6em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {meetingItem.subject}
            </Typography>
            <Chip
              label={status}
              size="small"
              sx={{
                backgroundColor: statusConfig.bg,
                color: statusConfig.text,
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "capitalize",
                borderRadius: "6px",
                height: 24,
              }}
            />
          </Stack>

          {/* Metadata Display Columns */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "flex-end" }}
            spacing={2}
          >
            {/* Left Data Blocks */}
            <Stack spacing={1} sx={{ color: "#64748b" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {meetingItem.selectedDate
                    ? new Date(meetingItem.selectedDate).toLocaleDateString(
                        undefined,
                        { weekday: "short", month: "short", day: "numeric" }
                      )
                    : "No date set"}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <AlarmOnIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {meetingItem.selectedDate
                    ? new Date(meetingItem.selectedDate).toLocaleTimeString(
                        undefined,
                        { hour: "2-digit", minute: "2-digit", hour12: true }
                      )
                    : ""}
                  {meetingItem.duration
                    ? ` • ${meetingItem.duration} mins`
                    : ""}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <AlignVerticalBottomIcon
                  sx={{ fontSize: 16, color: "#94a3b8" }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    textTransform: "capitalize",
                    color: "primary.main",
                  }}
                >
                  {meetingItem.meeting_type || "Virtual"}
                </Typography>
              </Stack>
            </Stack>

            {/* Right Grouped Participants Avatars */}
            {members.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: "#94a3b8",
                    mb: 0.75,
                    letterSpacing: 0.5,
                    textTransform: "uppercase",
                  }}
                >
                  Roster ({members.length})
                </Typography>
                <AvatarGroup
                  max={4}
                  spacing="medium"
                  sx={{
                    "& .MuiAvatar-root": {
                      width: 32,
                      height: 32,
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      border: "2px solid #ffffff",
                    },
                  }}
                >
                  {members.map((item, index) => (
                    <Avatar
                      key={index}
                      alt={`${item.firstName || "User"}`}
                      src={item.profile_image}
                    />
                  ))}
                </AvatarGroup>
              </Box>
            )}
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
};

export default MeetingCard;
